import Analytics from 'analytics'
import googleAnalytics from '@analytics/google-analytics'
import googleTagManager from '@analytics/google-tag-manager'
import * as Sentry from '@sentry/react'
import facebookPixel from './plugins/facebookPixel'
import sentryDebug from './plugins/sentryDebug'

let analyticsReadyResolve
let analyticsReadyReject
window.analyticsReady = new Promise((resolve, reject) => {
  analyticsReadyResolve = resolve
  analyticsReadyReject = reject
})

const reportError = (error, context = {}) => {
  console.error('[Analytics Error]', error, context)
  Sentry.withScope(scope => {
    scope.setTag('analytics_error', true)
    scope.setLevel('error')
    scope.setContext('analytics', context)
    Sentry.captureException(error)
  })
}

const wrapWithErrorHandler = analytics => {
  const originalTrack = analytics.track.bind(analytics)
  const originalPage = analytics.page.bind(analytics)
  const originalIdentify = analytics.identify.bind(analytics)

  analytics.track = (eventName, properties, options, callback) => {
    try {
      return originalTrack(eventName, properties, options, callback)
    } catch (error) {
      reportError(error, { method: 'track', eventName, properties })
    }
  }

  analytics.page = (data, options, callback) => {
    try {
      return originalPage(data, options, callback)
    } catch (error) {
      reportError(error, { method: 'page', data })
    }
  }

  analytics.identify = (userId, traits, options, callback) => {
    try {
      return originalIdentify(userId, traits, options, callback)
    } catch (error) {
      reportError(error, { method: 'identify', userId, traits })
    }
  }

  return analytics
}

const initAnalytics = () => {
  try {
    const googleAnalyticsId = document.querySelector(
      'meta[name="google-analytics-id"]'
    )?.content
    const facebookPixelId = document.querySelector(
      'meta[name="facebook-pixel-id"]'
    )?.content
    const tagManagerId = document.querySelector('meta[name="tag-manager-key"]')
      ?.content

    console.log('[Analytics] Initializing with:', {
      googleAnalyticsId: googleAnalyticsId || 'not configured',
      facebookPixelId: facebookPixelId || 'not configured',
      tagManagerId: tagManagerId || 'not configured',
    })

    const plugins = []

    if (googleAnalyticsId) {
      plugins.push(
        googleAnalytics({
          measurementIds: [googleAnalyticsId],
          dataLayerName: 'dataLayer',
        })
      )
    }

    if (tagManagerId) {
      plugins.push(googleTagManager({ containerId: tagManagerId }))
    }

    if (facebookPixelId) {
      plugins.push(facebookPixel({ trackingId: facebookPixelId }))
    }

    plugins.push(sentryDebug())

    const analytics = Analytics({
      app: 'Clearworks',
      plugins,
    })

    window.analytics = wrapWithErrorHandler(analytics)
    analyticsReadyResolve(window.analytics)
    window.analytics.page()

    console.log('[Analytics] Initialization complete')

    const gaCookie = document.cookie
      .split(';')
      .find(c => c.trim().startsWith('_ga='))
    if (gaCookie) {
      const clientId = gaCookie.split('=')[1]?.substring(6) || ''
      const clientIdInput = document.getElementById('hidden-google-client-id')
      if (clientIdInput) {
        clientIdInput.value = clientId
      }
    }
  } catch (error) {
    reportError(error, { method: 'initAnalytics', phase: 'initialization' })
    analyticsReadyReject(error)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAnalytics)
} else {
  initAnalytics()
}
